/**
 * The orders list, with its filters: Route (Buy · Make · From stock),
 * Supplier, Status and Health, plus a search. Filters live in the URL, so a
 * filtered list is a link; they run on the server (`listOrders`), and this
 * never filters the response (docs/list-endpoints.md). Changing a filter goes
 * back to page 1.
 *
 * Self-loading: the Orders page renders it across every customer in the
 * caller's scope, and the customer's Orders tab renders it fixed to one.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { ListResponse, OrderListOptions } from "@shared-core/data/adapter";
import type {
  OrderHealth,
  OrderLineRoute,
  OrderListItem,
  OrderStatus,
  OrderSupplier,
} from "@shared-core/data/order-to-invoice-types";
import {
  formatDateOrDash,
  formatQuotedPrice,
} from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { Alert, AlertDescription } from "../../primitives/alert";
import { Input } from "../../primitives/input";
import { Skeleton } from "../../primitives/skeleton";
import { ListPagination } from "../../patterns/ListPagination";
import { HealthPill, OrderStatusPill } from "./OrderPanels";

/**
 * The filters' own choices. A filter is offered before any row arrives, so
 * these are the list's static copy; every row's labels are the backend's.
 */
const ROUTE_FILTERS: Array<{ value: OrderLineRoute; label: string }> = [
  { value: "buy", label: "Buy" },
  { value: "make", label: "Make" },
  { value: "from_stock", label: "From stock" },
];
const STATUS_FILTERS: Array<{ value: OrderStatus; label: string }> = [
  { value: "ordered", label: "Ordered" },
  { value: "in_production", label: "In production" },
  { value: "despatched", label: "Despatched" },
  { value: "delivered", label: "Delivered" },
  { value: "received_into_stock", label: "Received into stock" },
];
const HEALTH_FILTERS: Array<{ value: OrderHealth; label: string }> = [
  { value: "late", label: "Late" },
  { value: "at_risk", label: "At risk" },
];
const ROUTES = ROUTE_FILTERS.map((f) => f.value);
const STATUSES = STATUS_FILTERS.map((f) => f.value);
const HEALTHS = HEALTH_FILTERS.map((f) => f.value);

function oneOf<T extends string>(
  value: string | null,
  allowed: T[],
): T | undefined {
  return allowed.includes(value as T) ? (value as T) : undefined;
}

const selectClass = "h-9 rounded-md border border-border bg-card px-3 text-sm";

export function OrdersList({ customerCode }: { customerCode?: string }) {
  const adapter = useDataAdapter();
  const [params, setParams] = useSearchParams();
  const route = oneOf(params.get("route"), ROUTES);
  const status = oneOf(params.get("status"), STATUSES);
  const health = oneOf(params.get("health"), HEALTHS);
  const supplier = params.get("supplier") || undefined;
  const q = params.get("q") || undefined;
  const page = Number(params.get("page")) || 1;
  const isFiltered = !!(route || status || health || supplier || q);

  const [suppliers, setSuppliers] = useState<OrderSupplier[]>([]);
  const [result, setResult] = useState<ListResponse<OrderListItem> | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // A customer's Orders tab offers only the suppliers on their orders.
    adapter
      .listOrderSuppliers(customerCode)
      .then((s) => !cancelled && setSuppliers(s))
      .catch(() => !cancelled && setSuppliers([]));
    return () => {
      cancelled = true;
    };
  }, [adapter, customerCode]);

  useEffect(() => {
    let cancelled = false;
    const options: OrderListOptions = {
      customerCode,
      route: route ? [route] : undefined,
      supplierId: supplier ? [supplier] : undefined,
      status,
      health,
      q,
      page,
    };
    setError(null);
    setResult(null);
    adapter
      .listOrders(options)
      .then((r) => !cancelled && setResult(r))
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Could not load orders.");
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, customerCode, route, supplier, status, health, q, page]);

  const setFilter = (key: string, value: string) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        if (key !== "page") next.delete("page");
        return next;
      },
      { replace: true },
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          // Uncontrolled, so remount on a new q: back and forward show the URL's search.
          key={q ?? ""}
          type="search"
          placeholder="Search orders"
          aria-label="Search orders"
          defaultValue={q ?? ""}
          onKeyDown={(e) => {
            if (e.key === "Enter") setFilter("q", e.currentTarget.value.trim());
          }}
          className="w-full sm:w-56"
        />
        <select
          aria-label="Route"
          value={route ?? ""}
          onChange={(e) => setFilter("route", e.target.value)}
          className={selectClass}
        >
          <option value="">All routes</option>
          {ROUTE_FILTERS.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
        <select
          aria-label="Supplier"
          value={supplier ?? ""}
          onChange={(e) => setFilter("supplier", e.target.value)}
          className={selectClass}
        >
          <option value="">All suppliers</option>
          {suppliers.map((s) => (
            <option key={s.supplier_id} value={s.supplier_id}>
              {s.name ?? s.supplier_no ?? s.supplier_id}
            </option>
          ))}
        </select>
        <select
          aria-label="Status"
          value={status ?? ""}
          onChange={(e) => setFilter("status", e.target.value)}
          className={selectClass}
        >
          <option value="">Any status</option>
          {STATUS_FILTERS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <select
          aria-label="Health"
          value={health ?? ""}
          onChange={(e) => setFilter("health", e.target.value)}
          className={selectClass}
        >
          <option value="">Any health</option>
          {HEALTH_FILTERS.map((h) => (
            <option key={h.value} value={h.value}>
              {h.label}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <Alert variant="destructive" role="alert">
          <AlertDescription>Could not load orders: {error}</AlertDescription>
        </Alert>
      )}
      {!result && !error && (
        <div className="space-y-2" aria-label="Loading orders">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      )}
      {result && result.items.length === 0 && (
        <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          {isFiltered
            ? "No orders match these filters."
            : "No orders yet. Orders appear here when an estimate is accepted or an order arrives from the portal or BrandHub."}
        </p>
      )}
      {result && result.items.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground">
                <tr className="text-left">
                  <th className="py-1.5 pr-3 font-normal">Order</th>
                  {!customerCode && (
                    <th className="py-1.5 pr-3 font-normal">Customer</th>
                  )}
                  <th className="py-1.5 pr-3 font-normal">What</th>
                  <th className="py-1.5 pr-3 font-normal">Route</th>
                  <th className="py-1.5 pr-3 font-normal">Status</th>
                  <th className="py-1.5 pr-3 font-normal">Needed by</th>
                  <th className="py-1.5 text-right font-normal">Value</th>
                </tr>
              </thead>
              <tbody>
                {result.items.map((row) => (
                  <tr
                    key={row.order_no}
                    className="border-t border-border align-top"
                  >
                    <td className="py-2 pr-3 font-medium tabular-nums">
                      <Link
                        to={paths.orderDetail({ orderNo: row.order_no })}
                        className="text-[color:var(--color-primary)] hover:underline"
                      >
                        {row.order_no}
                      </Link>
                    </td>
                    {!customerCode && (
                      <td className="py-2 pr-3">
                        <Link
                          to={paths.customerDetail({
                            customerId: row.customer_code,
                          })}
                          className="hover:underline"
                        >
                          {row.customer_name ?? row.customer_code}
                        </Link>
                      </td>
                    )}
                    <td className="min-w-48 py-2 pr-3">
                      <span className="block">{row.title}</span>
                      {row.project && (
                        <span className="block text-xs text-muted-foreground">
                          {row.project.project_no} · {row.project.title}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-muted-foreground">
                      {row.route_labels.length > 0
                        ? row.route_labels.join(", ")
                        : "—"}
                      {row.suppliers.length > 0 && (
                        <span className="block text-xs">
                          {row.suppliers
                            .map((s) => s.name ?? s.supplier_no)
                            .join(", ")}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <span className="flex flex-wrap gap-1">
                        <OrderStatusPill
                          status={row.status}
                          label={row.status_label}
                        />
                        <HealthPill
                          health={row.health}
                          label={row.health_label}
                        />
                      </span>
                    </td>
                    <td className="whitespace-nowrap py-2 pr-3 tabular-nums">
                      {formatDateOrDash(row.required_on)}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {formatQuotedPrice(row.value_pence)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ListPagination
            page={result.page}
            totalPages={result.totalPages}
            onPageChange={(p) => setFilter("page", String(p))}
          />
        </>
      )}
    </div>
  );
}
